// src/components/common/AvatarImage.tsx
import React, { CSSProperties, useEffect, useMemo, useState } from "react";

export type AvatarFit = "cover" | "contain";

export type AvatarImageProps = {
  src?: string | null;
  alt?: string;
  title?: string;

  // Use ONE of these approaches:
  size?: number | string; // sets both width+height
  width?: number | string;
  height?: number | string;

  fit?: AvatarFit; // cover crops, contain shows whole image
  position?: string;

  radius?: number | string;
  background?: string;
  border?: string;

  className?: string;
  style?: CSSProperties;
  imgStyle?: CSSProperties;

  fallback?: React.ReactNode;
};

export default function AvatarImage({
  src,
  alt = "avatar",
  title,

  size,
  width,
  height,

  // ✅ IMPORTANT CHANGE: default to "contain" so avatars never crop by accident
  fit = "contain",
  position = "center",

  radius = 12,
  background = "#f3f6f9",
  border = "1px solid #e8eef5",

  className,
  style,
  imgStyle,

  fallback,
}: AvatarImageProps) {
  const w = size ?? width;
  const h = size ?? height ?? w;

  const [ok, setOk] = useState(true);
  useEffect(() => setOk(true), [src]);

  const wrapperStyle: CSSProperties = useMemo(
    () => ({
      width: w,
      height: h,
      borderRadius: radius,
      overflow: "hidden",
      background,
      border,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "0 0 auto",
      position: "relative",
      // helps reduce “shrink weirdness” in tight flex rows
      minWidth: typeof w === "number" ? w : undefined,
      minHeight: typeof h === "number" ? h : undefined,
      ...style,
    }),
    [w, h, radius, background, border, style]
  );

  const showImg = !!src && ok;

  return (
    <div className={className} style={wrapperStyle} title={title || alt}>
      {showImg ? (
        <img
          src={src as string}
          alt={alt}
          draggable={false}
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: fit,
            objectPosition: position,
            display: "block",
            // prevents “inline image baseline gaps” and some browser quirks
            lineHeight: 0,
            ...imgStyle,
          }}
          onError={() => setOk(false)}
        />
      ) : (
        fallback ?? (
          <span style={{ fontWeight: 900, opacity: 0.85 }}>
            {(alt || "?").slice(0, 1).toUpperCase()}
          </span>
        )
      )}
    </div>
  );
}
